import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import PageShell, { Reveal, SectionHeading, Tags } from "../components/PageShell";
import { profile, projects, projectCategories } from "../data/profile";
import "../styles/Projects.scss";

const langColors = {
  JavaScript: "#f1e05a",
  TypeScript: "#3178c6",
  Python: "#3572A5",
  PHP: "#8892be",
  Dart: "#00B4AB",
  Java: "#b07219",
  "C++": "#f34b7d",
  HTML: "#e34c26",
  CSS: "#663399",
  Kotlin: "#A97BFF",
  "Jupyter Notebook": "#DA5B0B",
};

const prettyName = (name) =>
  name
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\s+/g, " ")
    .trim();

function ProjectCard({ project, index }) {
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className={`card project-card ${project.featured && index === 0 ? "is-featured" : ""}`}
    >
      <div className="project-top">
        <span className="project-index">P/{String(index + 1).padStart(2, "0")}</span>
        <span className="project-period">{project.period}</span>
        {project.status && (
          <span className="badge">
            <span className="pulse" /> {project.status}
          </span>
        )}
      </div>

      <div className="project-main">
        <div className="project-head">
          <h3>{project.name}</h3>
          <p className="project-subtitle">{project.subtitle}</p>
          <p className="project-role">{project.role}</p>
          <p className="project-summary">{project.summary}</p>
        </div>

        <div className="project-metrics">
          {project.metrics.map((m) => (
            <div key={m.label} className="metric">
              <span className="stat-value">{m.value}</span>
              <span className="metric-label">{m.label}</span>
            </div>
          ))}
        </div>
      </div>

      <ul className="bullets">
        {project.points.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>

      <div className="project-foot">
        <Tags items={project.stack} />
        {project.links.length > 0 && (
          <div className="project-links">
            {project.links.map((l) => (
              <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" className="text-link">
                {l.label} ↗
              </a>
            ))}
          </div>
        )}
      </div>
    </motion.article>
  );
}

function GithubRepos() {
  const [state, setState] = useState({ status: "loading", repos: [] });

  useEffect(() => {
    const controller = new AbortController();
    fetch(`https://api.github.com/users/${profile.githubUser}/repos?sort=updated&per_page=30`, {
      signal: controller.signal,
      headers: { Accept: "application/vnd.github+json" },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`GitHub API ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!Array.isArray(data)) throw new Error("Unexpected response");
        const repos = data
          .filter((r) => !r.fork && r.name.toLowerCase() !== profile.githubUser.toLowerCase())
          .slice(0, 6)
          .map((r) => ({
            id: r.id,
            name: prettyName(r.name),
            description: r.description,
            url: r.html_url,
            homepage: r.homepage,
            language: r.language,
            stars: r.stargazers_count,
            updated: new Date(r.pushed_at || r.updated_at).toLocaleDateString("en-US", {
              month: "short",
              year: "numeric",
            }),
          }));
        setState({ status: repos.length ? "ok" : "empty", repos });
      })
      .catch((err) => {
        if (err.name !== "AbortError") setState({ status: "error", repos: [] });
      });
    return () => controller.abort();
  }, []);

  if (state.status === "loading") {
    return (
      <div className="repo-grid">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="card repo-card is-skeleton" aria-hidden="true" />
        ))}
      </div>
    );
  }

  if (state.status !== "ok") {
    return (
      <div className="card repo-fallback">
        <p>
          {state.status === "error"
            ? "Couldn't load repositories right now."
            : "No public repositories to show yet."}
        </p>
        <a className="btn btn-ghost" href={profile.github} target="_blank" rel="noopener noreferrer">
          Open GitHub profile ↗
        </a>
      </div>
    );
  }

  return (
    <div className="repo-grid">
      {state.repos.map((r, i) => (
        <Reveal key={r.id} delay={i * 0.05}>
          <a className="card card-hover repo-card" href={r.url} target="_blank" rel="noopener noreferrer">
            <div className="repo-top">
              <span className="repo-name">{r.name}</span>
              <span aria-hidden="true">↗</span>
            </div>
            <p>{r.description || "No description yet."}</p>
            <div className="repo-meta">
              {r.language && (
                <span>
                  <i style={{ background: langColors[r.language] || "#a09bb3" }} />
                  {r.language}
                </span>
              )}
              <span>★ {r.stars}</span>
              <span>{r.updated}</span>
            </div>
          </a>
        </Reveal>
      ))}
    </div>
  );
}

function Projects() {
  const [active, setActive] = useState("all");
  const list = active === "all" ? projects : projects.filter((p) => p.categories.includes(active));

  return (
    <PageShell slug="projects">
      <section>
        <SectionHeading eyebrow="Selected work" title="Projects with real constraints — and real numbers">
          Enterprise compliance, offline AI and mobile apps. Each card lists my role, the stack and the
          measurable outcome.
        </SectionHeading>

        <div className="filter-chips" role="tablist" aria-label="Filter projects">
          {projectCategories.map((c) => {
            const count =
              c.key === "all" ? projects.length : projects.filter((p) => p.categories.includes(c.key)).length;
            return (
              <button
                key={c.key}
                role="tab"
                aria-selected={active === c.key}
                className={active === c.key ? "is-active" : ""}
                onClick={() => setActive(c.key)}
              >
                {c.label}
                <span>{count}</span>
              </button>
            );
          })}
        </div>

        <motion.div layout className="project-list">
          <AnimatePresence mode="popLayout">
            {list.map((p) => (
              <ProjectCard key={p.slug} project={p} index={projects.indexOf(p)} />
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <section>
        <SectionHeading eyebrow="Open source" title="Latest on GitHub">
          Pulled live from{" "}
          <a className="text-link" href={profile.github} target="_blank" rel="noopener noreferrer">
            github.com/{profile.githubUser}
          </a>
          .
        </SectionHeading>
        <GithubRepos />
      </section>
    </PageShell>
  );
}

export default Projects;
